/**
 * Universal GLB loader for vehicles and props.
 * Character loading is handled by load.ts (needs AnimationMixer).
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { SceneBudget } from '../systems/scene';

const loader = new GLTFLoader();

export interface LoadedAsset {
  scene: THREE.Group;
  url: string;
}

/** Load a GLB and return its scene group. */
export async function loadGLB(url: string): Promise<LoadedAsset> {
  const gltf = await loader.loadAsync(url);
  return { scene: gltf.scene as THREE.Group, url };
}

/** Load multiple GLBs in parallel. */
export async function loadAllGLBs(urls: string[]): Promise<LoadedAsset[]> {
  return Promise.all(urls.map((url) => loadGLB(url)));
}

/**
 * Count meshes (draw calls) in a Three.js group.
 */
export function countDrawCalls(obj: THREE.Object3D): number {
  let count = 0;
  obj.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) count++;
  });
  return count;
}

/**
 * Count textures in a Three.js group.
 */
export function countTextures(obj: THREE.Object3D): number {
  const seen = new Set<THREE.Texture>();
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mat = mesh.material;
    const materials = Array.isArray(mat) ? mat : [mat];
    for (const m of materials) {
      if (!m) continue;
      const matAny = m as unknown as Record<string, unknown>;
      for (const key of Object.keys(matAny)) {
        const val = matAny[key];
        if (val instanceof THREE.Texture) seen.add(val);
      }
    }
  });
  return seen.size;
}

/**
 * Add a loaded asset to the scene with budget tracking.
 * Returns the number of new draw calls added.
 */
export function placeAsset(
  scene: THREE.Scene,
  asset: LoadedAsset,
  position: THREE.Vector3,
  budget: SceneBudget,
): number {
  asset.scene.position.copy(position);
  const draws = countDrawCalls(asset.scene);
  const tex = countTextures(asset.scene);
  budget.drawCalls += draws;
  budget.textures += tex;
  scene.add(asset.scene);

  if (budget.drawCalls > budget.maxDrawCalls) {
    console.warn(
      `[budget] draw calls ${budget.drawCalls} > max ${budget.maxDrawCalls}`,
    );
  }
  if (budget.textures > budget.maxTextures) {
    console.warn(
      `[budget] textures ${budget.textures} > max ${budget.maxTextures}`,
    );
  }
  return draws;
}